import type { FileDiff } from "@/lib/types";

// A parsed row of a unified diff. Hunk headers become separators; everything else carries the
// old/new line numbers so the view reads like GitHub rather than raw `difflib` output.
type Row =
  | { kind: "hunk"; text: string }
  | { kind: "add" | "del" | "ctx"; oldNo: number | null; newNo: number | null; text: string };

// Turn a `difflib.unified_diff` string into displayable rows. The redundant `--- a/…` / `+++ b/…`
// file headers are dropped (the card header already names the file); `@@ -a,b +c,d @@` seeds the
// running line numbers and becomes a subtle separator carrying any section context after the `@@`.
function parseDiff(diff: string): Row[] {
  const lines = diff.split("\n");
  if (lines.length && lines[lines.length - 1] === "") lines.pop(); // trailing newline artifact
  const rows: Row[] = [];
  let oldNo = 0;
  let newNo = 0;
  let started = false;
  for (const raw of lines) {
    if (raw.startsWith("@@")) {
      started = true;
      const m = raw.match(/@@ -(\d+)(?:,\d+)? \+(\d+)(?:,\d+)? @@(.*)/);
      if (m) {
        oldNo = parseInt(m[1], 10);
        newNo = parseInt(m[2], 10);
        rows.push({ kind: "hunk", text: m[3].trim() });
      }
      continue;
    }
    if (!started && (raw.startsWith("--- ") || raw.startsWith("+++ "))) continue; // file headers
    if (raw.startsWith("\\")) continue; // "\ No newline at end of file"
    if (raw.startsWith("+")) {
      rows.push({ kind: "add", oldNo: null, newNo: newNo++, text: raw.slice(1) });
    } else if (raw.startsWith("-")) {
      rows.push({ kind: "del", oldNo: oldNo++, newNo: null, text: raw.slice(1) });
    } else {
      rows.push({ kind: "ctx", oldNo: oldNo++, newNo: newNo++, text: raw.startsWith(" ") ? raw.slice(1) : raw });
    }
  }
  return rows;
}

function counts(rows: Row[]): { adds: number; dels: number } {
  let adds = 0;
  let dels = 0;
  for (const r of rows) {
    if (r.kind === "add") adds++;
    else if (r.kind === "del") dels++;
  }
  return { adds, dels };
}

const STATUS_CLS: Record<string, string> = {
  added: "bg-green-500/20 text-green-400",
  modified: "bg-yellow-500/20 text-yellow-300",
  deleted: "bg-red-500/20 text-red-400",
};

const GUTTER = "w-10 shrink-0 select-none border-r border-black/10 px-2 text-right text-text/30 dark:border-white/10";

function DiffRow({ row }: { row: Row }) {
  if (row.kind === "hunk") {
    return (
      <div className="flex select-none items-center gap-2 bg-accent/[0.07] px-3 py-1 text-[11px] text-accent/80">
        <span aria-hidden>⋯</span>
        <span className="truncate">{row.text || "changes"}</span>
      </div>
    );
  }
  const tone = row.kind === "add" ? "bg-green-500/[0.08]" : row.kind === "del" ? "bg-red-500/[0.08]" : "";
  const sign = row.kind === "add" ? "+" : row.kind === "del" ? "−" : " ";
  const signCls = row.kind === "add" ? "text-green-400" : row.kind === "del" ? "text-red-400" : "text-text/20";
  const codeCls = row.kind === "add" ? "text-green-300" : row.kind === "del" ? "text-red-300" : "text-text/70";
  return (
    <div className={`flex ${tone}`}>
      <span className={GUTTER}>{row.newNo ?? ""}</span>
      <span className={`w-5 shrink-0 select-none text-center ${signCls}`}>{sign}</span>
      <span className={`whitespace-pre px-2 ${codeCls}`}>{row.text || " "}</span>
    </div>
  );
}

export default function DiffView({ files }: { files: FileDiff[] }) {
  return (
    <div className="space-y-3">
      {files.map((f) => {
        const isBinary = f.diff === "(binary file)";
        const rows = isBinary ? [] : parseDiff(f.diff);
        const { adds, dels } = counts(rows);
        return (
          <div key={f.path} className="overflow-hidden rounded-lg border border-black/10 dark:border-white/10">
            <div className="flex items-center gap-2 border-b border-black/10 bg-black/[0.02] px-3 py-1.5 text-sm dark:border-white/10 dark:bg-white/5">
              <span className={`rounded px-1.5 py-0.5 text-xs ${STATUS_CLS[f.status] ?? "bg-white/10"}`}>
                {f.status}
              </span>
              <span className="min-w-0 flex-1 truncate font-mono text-text/80">{f.path}</span>
              {!isBinary && (adds > 0 || dels > 0) && (
                <span className="shrink-0 font-mono text-xs">
                  {adds > 0 && <span className="text-green-400">+{adds}</span>}
                  {adds > 0 && dels > 0 && " "}
                  {dels > 0 && <span className="text-red-400">−{dels}</span>}
                </span>
              )}
            </div>
            {isBinary ? (
              <div className="px-3 py-2 text-xs text-text/40">(binary file)</div>
            ) : (
              <div className="overflow-x-auto font-mono text-xs leading-5">
                {rows.map((r, i) => (
                  <DiffRow key={i} row={r} />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
